import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar, Button, Image, Row, Card, Select, Tooltip, Typography, Col, Modal } from 'antd';
import { EditOutlined, DeleteOutlined, FolderAddOutlined } from '@ant-design/icons';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { prepareMenuListData, getProductsCount } from '../../utils/menu';
import { getImageFallbackUrl } from '../../utils/helpers';

const { Meta } = Card;
const { Title, Text } = Typography;
const { Option } = Select;

function Menu({ history }) {
  const navigate = useNavigate();
  const { setHeaderComponent } = useContext(LayoutContext);
  const [menuList, setMenuList] = useState([]);
  const [mainLanId, setMainLanId] = useState();
  const [menuData, setMenuData] = useState();
  const [selectedItemDelete, setSelectedItemToDelete] = useState();
  const [userSettings, setUserSettings] = useState({
    is_showing_subcategories: true,
    is_showing_brands: true,
  });
  const { doFetch: fetchMenu } = useFetch({
    defaultValue: { categories: [], languages: [] },
  });
  const { doFetch: fetchDelete } = useFetch();
  const { doFetch: fetchUserSettings } = useFetch();

  const getMenuList = data => {
    setMenuData(data);
    const selectedLanId = mainLanId || data.languages[1].id;
    if (!mainLanId) {
      setMainLanId(data.languages[1].id); // 0 For English, 1 For Arabic, 2 For Kurdish.
    }
    setMenuList(prepareMenuListData(data, selectedLanId));
  };

  const getProductsFilter = () => {
    if (!menuData || !mainLanId) return null;
    return (
      <div className="menu-header-section">
        {/* Filter Controls Row */}
        <div className="menu-header-filters">
          <Row gutter={[16, 8]} align="middle" className="filter-row">
            <Col xs={24} sm={12} md={8}>
              <div className="filter-item responsive-filter-item">
                <b>Products count: {getProductsCount(menuData)}</b>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="filter-item responsive-filter-item">
                <b>Selected language:</b>
                <Select
                  value={mainLanId}
                  className="filter-select responsive-filter-select"
                  onChange={lanId => setMainLanId(lanId)}
                  size="small"
                >
                  {menuData.languages.map(language => (
                    <Option value={language.id} key={language.id}>
                      {language.name}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
            {/* <Col xs={24} sm={24} md={8}>
              <div className="filter-item responsive-filter-item">
                <b>Search by name [WIP]:</b>
                <Input
                  className="filter-by-name responsive-filter-input"
                  onChange={() => {}}
                  placeholder="product name"
                  size="small"
                />
              </div>
            </Col> */}
          </Row>
        </div>

        {/* Action Buttons Row */}
        <div className="menu-action-buttons-wrapper">
          <Row
            type="flex"
            justify="end"
            gutter={[8, 8]}
            className="menu-action-buttons"
          >
            <Col xs={24} sm={12} md={6}>
              <Button
                type="primary"
                icon={<FolderAddOutlined />}
                onClick={() => navigate('/product-create')}
                className="menu-action-btn"
              >
                Add New Product
              </Button>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Button
                type="primary"
                icon={<FolderAddOutlined />}
                onClick={() => navigate('/category-create')}
                className="menu-action-btn"
              >
                Add New Category
              </Button>
            </Col>
            {userSettings.is_showing_subcategories && (
              <Col xs={24} sm={12} md={6}>
                <Button
                  type="primary"
                  icon={<FolderAddOutlined />}
                  onClick={() => navigate('/subcategory-create')}
                  className="menu-action-btn"
                >
                  Add New Subcategory
                </Button>
              </Col>
            )}
            {userSettings.is_showing_brands && (
              <Col xs={24} sm={12} md={6}>
                <Button
                  type="primary"
                  icon={<FolderAddOutlined />}
                  onClick={() => navigate('/brand-create')}
                  className="menu-action-btn"
                >
                  Add New Brand
                </Button>
              </Col>
            )}
          </Row>
        </div>
      </div>
    );
  };

  useEffect(() => {
    // DEPRECATED: Commented out to avoid calling /menu endpoint
    // fetchMenu({
    //   url: URLS.menu,
    //   onSuccess: getMenuList,
    // });

    // Fetch user settings for conditional button display
    fetchUserSettings({
      url: URLS.userSettings,
      onSuccess: (data) => {
        setUserSettings({
          is_showing_subcategories: data.is_showing_subcategories !== false,
          is_showing_brands: data.is_showing_brands !== false,
        });
      },
      onError: () => {
        // Use default settings if fetch fails
        setUserSettings({
          is_showing_subcategories: true,
          is_showing_brands: true,
        });
      },
    });

    // Clear any existing header component when this page loads
    setHeaderComponent(null);

    return () => {
      setHeaderComponent(null);
    };
  }, [fetchMenu, fetchUserSettings, setHeaderComponent]);

  useEffect(() => {
    if (mainLanId && menuData) {
      getMenuList(menuData);
      // No longer setting header component - will render inline instead
    }
  }, [mainLanId, menuData]);

  const handleItemDelete = () => {
    fetchDelete({
      url: URLS.deleteProductFetch({
        productId: selectedItemDelete.id,
        categoryId: selectedItemDelete.category_id,
      }),
      method: 'DELETE',
      onSuccess: () => {
        // DEPRECATED: Commented out to avoid calling /menu endpoint
        // fetchMenu({
        //   url: URLS.menu,
        //   onSuccess: getMenuList,
        // });
        setSelectedItemToDelete();
      },
    });
  };

  return (
    <div className="menu-page-container">
      {/* Render filters and actions directly in page content */}
      {menuData && mainLanId && getProductsFilter()}

      {/* Categories and Products Section */}
      <div className="menu-content-section">
        {menuList.map(cat => (
          <Card
            key={cat.id}
            type="inner"
            title={
              <div className="category-card-header responsive-category-header">
                <Avatar
                  size={{ xs: 45, sm: 55, md: 65 }}
                  src={cat.image}
                  className="category-avatar"
                />
                <div className="category-info">
                  <Tooltip
                    placement="top"
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: cat.nameTranslations,
                        }}
                      />
                    }
                  >
                    <Title level={4} className="category-title">{cat.name}</Title>
                  </Tooltip>
                  {/* {cat.tags.map(tag => (
                  <Tooltip
                    placement="top"
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: tag.nameTranslations,
                        }}
                      />
                    }>
                    <Text code>{tag.name}</Text>
                  </Tooltip>
                ))} */}
                </div>
              </div>
            }
            extra={
              <>
                <Tooltip placement="top" title="Edit Category">
                  <Link to={`category/${cat.id}`} className="category-edit-link">
                    <Text>
                      <EditOutlined
                        type="edit"
                        key="edit"
                        className="category-edit-icon"
                      />
                    </Text>
                  </Link>
                </Tooltip>
              </>
            }
            className="m-b-30 category-card responsive-category-card"
          >
            <Row gutter={[16, 16]} className="product-grid">
              {cat.products.map(product => (
                <Col
                  xs={24}
                  sm={12}
                  md={8}
                  lg={6}
                  xl={4}
                  key={product.id}
                >
                  <Card
                    className="product-card responsive-product-card"
                    cover={
                      <div className="product-card-image-wrapper">
                        <img
                          alt={product.name || "product image"}
                          className="product-card-image"
                          src={product.image}
                          onError={(e) => {
                            const fallbackUrl = getImageFallbackUrl(e.target.src);
                            if (fallbackUrl !== e.target.src) {
                              e.target.src = fallbackUrl;
                            } else {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                        <div className="product-image-placeholder" style={{ display: 'none' }}>
                          <span>No Image</span>
                        </div>
                      </div>
                    }
                    actions={[
                      <Tooltip placement="top" title="Delete">
                        <DeleteOutlined
                          type="delete"
                          key="delete"
                          onClick={() => setSelectedItemToDelete(product)}
                          className="product-action-icon"
                        />
                      </Tooltip>,
                      <Tooltip placement="top" title="Update">
                        <Link to={`product/${product.id}`}>
                          <EditOutlined
                            type="edit"
                            key="edit"
                            className="product-action-icon"
                          />
                        </Link>
                      </Tooltip>,
                    ]}
                  >
                    <Meta
                      title={
                        <Tooltip
                          placement="top"
                          title={
                            <div
                              dangerouslySetInnerHTML={{
                                __html: product.nameTranslations,
                              }}
                            />
                          }
                        >
                          <div className="product-title">{product.name}</div>
                        </Tooltip>
                      }
                      description={
                        <div className="product-price">
                          {`${product.price} ${product.currency}`}
                        </div>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        title={`Delete product: ${selectedItemDelete?.name}`}
        open={!!selectedItemDelete?.id}
        onOk={handleItemDelete}
        onCancel={() => setSelectedItemToDelete()}
      >
        Do you confirm deleteing the product?
      </Modal>
    </div>
  );
}

export default Menu;
